import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Briefcase, TrendingUp, ShieldCheck, Building2, Truck, Factory, HardHat, Users, BarChart3, Tag, Sparkles, X, Check, Minus, BarChart } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import React from "react";
import API from "@/lib/api";

const SERVICE_ICONS = ["BarChart3", "Briefcase", "TrendingUp", "Users", "ShieldCheck"];
const SERVICE_LABELS = {
  "ai-quick-scan": "Quick Scan",
  "ai-pilot": "Pilot",
  "ai-workflow": "Workflow",
  "ai-training": "Training",
  "ai-governance": "Governance",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" } }),
};

const sectorIcons = [Briefcase, BarChart3, Truck, Factory, HardHat, Users];

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const FALLBACK_BUNDLES = [
    { id: "starter", services: ["ai-quick-scan", "ai-training"], original_price: 5900, price: 5400, savings: 500,
      name: ({nl:"Starter Bundel",en:"Starter Bundle",de:"Starter-Paket",fr:"Pack Starter",es:"Paquete Inicial",ar:"\u0627\u0644\u062d\u0632\u0645\u0629 \u0627\u0644\u0623\u0648\u0644\u0649"})[lang] || "Starter Bundle",
      description: ({nl:"AI Quick Scan + AI Training",en:"AI Quick Scan + AI Training",de:"AI Quick Scan + KI Training",fr:"AI Quick Scan + Formation IA",es:"AI Quick Scan + Formaci\u00f3n IA",ar:"\u0645\u0633\u062d AI + \u062a\u062f\u0631\u064a\u0628 AI"})[lang] || "AI Quick Scan + AI Training" },
    { id: "growth", services: ["ai-quick-scan", "ai-pilot", "ai-training"], original_price: 10850, price: 9850, savings: 1000,
      name: ({nl:"Groei Bundel",en:"Growth Bundle",de:"Wachstums-Paket",fr:"Pack Croissance",es:"Paquete Crecimiento",ar:"\u062d\u0632\u0645\u0629 \u0627\u0644\u0646\u0645\u0648"})[lang] || "Growth Bundle",
      description: ({nl:"Scan + Pilot + Training",en:"Scan + Pilot + Training",de:"Scan + Pilot + Training",fr:"Scan + Pilot + Formation",es:"Scan + Piloto + Formaci\u00f3n",ar:"\u0645\u0633\u062d + \u062a\u062c\u0631\u0628\u0629 + \u062a\u062f\u0631\u064a\u0628"})[lang] || "Scan + Pilot + Training" },
    { id: "enterprise", services: ["ai-quick-scan", "ai-pilot", "ai-workflow", "ai-training", "ai-governance"], original_price: 27750, price: 22750, savings: 5000,
      name: ({nl:"Enterprise Bundel",en:"Enterprise Bundle",de:"Enterprise-Paket",fr:"Pack Entreprise",es:"Paquete Empresarial",ar:"\u062d\u0632\u0645\u0629 \u0627\u0644\u0645\u0624\u0633\u0633\u0627\u062a"})[lang] || "Enterprise Bundle",
      description: ({nl:"Alle 5 diensten",en:"All 5 services",de:"Alle 5 Dienste",fr:"Les 5 services",es:"Los 5 servicios",ar:"\u062c\u0645\u064a\u0639 \u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u062e\u0645\u0633"})[lang] || "All 5 services" },
    { id: "compliance", services: ["ai-governance", "ai-training"], original_price: 10900, price: 9400, savings: 1500,
      name: ({nl:"Compliance Bundel",en:"Compliance Bundle",de:"Compliance-Paket",fr:"Pack Conformit\u00e9",es:"Paquete Cumplimiento",ar:"\u062d\u0632\u0645\u0629 \u0627\u0644\u0627\u0645\u062a\u062b\u0627\u0644"})[lang] || "Compliance Bundle",
      description: ({nl:"AI Governance + Training",en:"AI Governance + Training",de:"KI Governance + Training",fr:"Gouvernance IA + Formation",es:"Gobernanza IA + Formaci\u00f3n",ar:"\u062d\u0648\u0643\u0645\u0629 AI + \u062a\u062f\u0631\u064a\u0628"})[lang] || "AI Governance + Training" },
  ];

  const [bundles, setBundles] = useState(FALLBACK_BUNDLES);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    API.get(`/bundles?lang=${lang}`)
      .then(res => { if (Array.isArray(res.data) && res.data.length > 0) setBundles(res.data); })
      .catch(() => { setBundles(FALLBACK_BUNDLES); });
  }, [lang]);

  const services = [
    { id: "ai-quick-scan", price: "1.950" },
    { id: "ai-pilot", price: "4.950" },
    { id: "ai-workflow", price: "9.950" },
    { id: "ai-training", price: "3.950" },
    { id: "ai-governance", price: "6.950" },
  ];

  const serviceNames = {
    nl: ["AI Quick Scan", "AI Pilot", "AI Workflow Automatisering", "AI Training", "AI Governance"],
    en: ["AI Quick Scan", "AI Pilot", "AI Workflow Automation", "AI Training", "AI Governance"],
    de: ["AI Quick Scan", "AI Pilot", "AI Workflow Automatisierung", "KI Training", "KI Governance"],
    fr: ["AI Quick Scan", "AI Pilot", "Automatisation de Workflow IA", "Formation IA", "Gouvernance IA"],
    es: ["AI Quick Scan", "AI Pilot", "Automatizaci\u00f3n de Workflow IA", "Formaci\u00f3n IA", "Gobernanza IA"],
    ar: ["\u0645\u0633\u062d AI \u0627\u0644\u0633\u0631\u064a\u0639", "\u062a\u062c\u0631\u0628\u0629 AI", "\u0623\u062a\u0645\u062a\u0629 \u0633\u064a\u0631 \u0627\u0644\u0639\u0645\u0644", "\u062a\u062f\u0631\u064a\u0628 AI", "\u062d\u0648\u0643\u0645\u0629 AI"],
  };
  const serviceDescs = {
    nl: [
      "In 2-3 weken laten wij zien waar AI direct rendement oplevert.",
      "We kiezen een proces, bouwen een pilot, laten uw team testen.",
      "AI koppelen aan uw CRM, ERP of ticketsysteem.",
      "Basistraining, prompttraining en AI-gebruiksbeleid.",
      "Inventarisatie, risicobeoordeling en AI Act readiness.",
    ],
    en: [
      "In 2-3 weeks we show where AI delivers immediate returns.",
      "We choose one process, build a pilot, let your team test.",
      "Connect AI to your CRM, ERP or ticket system.",
      "Basic training, prompt training and AI usage policy.",
      "Inventory, risk assessment and AI Act readiness.",
    ],
    de: [
      "In 2-3 Wochen zeigen wir, wo KI sofortige Rendite liefert.",
      "Wir w\u00e4hlen einen Prozess, bauen einen Piloten, lassen Ihr Team testen.",
      "KI mit Ihrem CRM, ERP oder Ticketsystem verbinden.",
      "Basistraining, Prompt-Training und KI-Nutzungsrichtlinien.",
      "Bestandsaufnahme, Risikobewertung und AI Act Bereitschaft.",
    ],
    fr: [
      "En 2-3 semaines, nous montrons o\u00f9 l'IA g\u00e9n\u00e8re un rendement imm\u00e9diat.",
      "Nous choisissons un processus, construisons un pilote, laissons votre \u00e9quipe tester.",
      "Connecter l'IA \u00e0 votre CRM, ERP ou syst\u00e8me de tickets.",
      "Formation de base, formation aux prompts et politique d'utilisation IA.",
      "Inventaire, \u00e9valuation des risques et pr\u00e9paration au AI Act.",
    ],
    es: [
      "En 2-3 semanas mostramos d\u00f3nde la IA genera rendimiento inmediato.",
      "Elegimos un proceso, construimos un piloto, su equipo prueba.",
      "Conectar IA a su CRM, ERP o sistema de tickets.",
      "Formaci\u00f3n b\u00e1sica, formaci\u00f3n en prompts y pol\u00edtica de uso de IA.",
      "Inventario, evaluaci\u00f3n de riesgos y preparaci\u00f3n para el AI Act.",
    ],
    ar: [
      "\u0641\u064a 2-3 \u0623\u0633\u0627\u0628\u064a\u0639 \u0646\u0648\u0636\u062d \u0623\u064a\u0646 \u064a\u062d\u0642\u0642 AI \u0639\u0648\u0627\u0626\u062f \u0641\u0648\u0631\u064a\u0629.",
      "\u0646\u062e\u062a\u0627\u0631 \u0639\u0645\u0644\u064a\u0629 \u0648\u0627\u062d\u062f\u0629\u060c \u0646\u0628\u0646\u064a \u062a\u062c\u0631\u0628\u0629\u060c \u0641\u0631\u064a\u0642\u0643 \u064a\u062e\u062a\u0628\u0631.",
      "\u0631\u0628\u0637 AI \u0628\u0646\u0638\u0627\u0645 CRM \u0623\u0648 ERP \u0623\u0648 \u0646\u0638\u0627\u0645 \u0627\u0644\u062a\u0630\u0627\u0643\u0631.",
      "\u062a\u062f\u0631\u064a\u0628 \u0623\u0633\u0627\u0633\u064a\u060c \u062a\u062f\u0631\u064a\u0628 \u0639\u0644\u0649 \u0627\u0644\u0623\u0648\u0627\u0645\u0631 \u0648\u0633\u064a\u0627\u0633\u0629 \u0627\u0633\u062a\u062e\u062f\u0627\u0645 AI.",
      "\u062c\u0631\u062f\u060c \u062a\u0642\u064a\u064a\u0645 \u0627\u0644\u0645\u062e\u0627\u0637\u0631 \u0648\u0627\u0644\u0627\u0633\u062a\u0639\u062f\u0627\u062f \u0644\u0642\u0627\u0646\u0648\u0646 AI.",
    ],
  };

  const handleServiceClick = (serviceId) => {
    if (user) {
      navigate(`/intake/${serviceId}`);
    } else {
      navigate("/register");
    }
  };

  const handleBundleClick = async (bundleId) => {
    if (!user) { navigate("/register"); return; }
    try {
      const { data } = await API.post("/payments/bundle-checkout", {
        bundle_id: bundleId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      navigate("/register");
    }
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-16" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/4bfdb3dd-17d6-4a59-9a29-86d4ffc17442/images/b1cf818ac58054565234e15ebdfe153d040416ea0369504d788b57240813db53.png)` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.div variants={fadeUp} custom={0} className="inline-block mb-6">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3] bg-[#0A3FA3]/10 px-4 py-2 border border-[#0A3FA3]/20" data-testid="hero-badge">
                {t("hero.badge")}
              </span>
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black leading-none text-[#0A0A0A] mb-6" data-testid="hero-title">
              {t("hero.title")}
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base sm:text-lg text-foreground/70 max-w-2xl mb-8 leading-relaxed" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-[#00BF4F] hover:bg-[#00A843] text-white rounded-sm px-8 py-3 text-sm font-bold tracking-wide" onClick={() => handleServiceClick("ai-quick-scan")} data-testid="hero-cta-scan">
                {t("hero.cta_scan")} <ArrowRight size={16} className="ml-2" />
              </Button>
              <a href="#services">
                <Button size="lg" variant="outline" className="rounded-sm px-8 py-3 text-sm font-bold tracking-wide border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="hero-cta-services">
                  {t("hero.cta_services")}
                </Button>
              </a>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="visible" className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t border-[#0A0A0A]/10">
            {[
              { val: "33%", label: t("hero.stat_ai") },
              { val: "90", label: t("hero.stat_days"), suffix: ({nl:" dagen",en:" days",de:" Tage",fr:" jours",es:" d\u00edas",ar:" \u064a\u0648\u0645\u0627\u064b"})[lang] || " days" },
              { val: "50+", label: t("hero.stat_fte") },
              { val: "30+", label: t("hero.stat_exp") },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="text-3xl sm:text-4xl font-black text-[#0A3FA3]">{s.val}{s.suffix && <span className="text-lg">{s.suffix}</span>}</div>
                <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3]">{t("services.title")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-3 text-[#0A0A0A]">{t("services.subtitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`${i < 2 ? "md:col-span-6" : "md:col-span-4"} group border border-border hover:border-[#0A3FA3] transition-colors duration-200 p-8 cursor-pointer`}
                onClick={() => handleServiceClick(s.id)}
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="text-xs tracking-[0.2em] font-bold text-muted-foreground">0{i + 1}</span>
                  <span className="text-sm font-bold text-[#0A3FA3]">&euro;{s.price}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 group-hover:text-[#0A3FA3] transition-colors">{serviceNames[lang][i]}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">{serviceDescs[lang][i]}</p>
                <div className="flex items-center gap-2 text-sm font-bold text-[#0A3FA3] opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("services.start_intake")} <ArrowRight size={14} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundles */}
      <section id="bundles" className="py-20 md:py-28 bg-[#0A0A0A]" data-testid="bundles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30]">
              {({nl:"Bundels",en:"Bundles",de:"Pakete",fr:"Packs",es:"Paquetes",ar:"\u0627\u0644\u062d\u0632\u0645"})[lang] || "Bundles"}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-3 text-white">
              {({nl:"Bespaar met onze bundels",en:"Save with our bundles",de:"Sparen Sie mit unseren Paketen",fr:"\u00c9conomisez avec nos packs",es:"Ahorre con nuestros paquetes",ar:"\u0648\u0641\u0631 \u0645\u0639 \u062d\u0632\u0645\u0646\u0627"})[lang] || "Save with our bundles"}
            </h2>
            <p className="text-white/50 mt-3 max-w-2xl mx-auto text-sm">
              {({nl:"Combineer diensten voor meer impact en bespaar tot \u20ac5.000",en:"Combine services for more impact and save up to \u20ac5,000",de:"Kombinieren Sie Dienste f\u00fcr mehr Wirkung und sparen Sie bis zu \u20ac5.000",fr:"Combinez les services pour plus d'impact et \u00e9conomisez jusqu'\u00e0 5 000\u20ac",es:"Combine servicios para m\u00e1s impacto y ahorre hasta \u20ac5.000",ar:"\u0627\u062c\u0645\u0639 \u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0644\u062a\u0623\u062b\u064a\u0631 \u0623\u0643\u0628\u0631 \u0648\u0648\u0641\u0631 \u062d\u062a\u0649 5000\u20ac"})[lang] || "Combine services for more impact and save up to \u20ac5,000"}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bundles.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative border ${b.id === "growth" ? "border-[#FF3B30] ring-1 ring-[#FF3B30]" : "border-white/10"} p-6 group hover:border-white/30 transition-colors`}
                data-testid={`bundle-card-${b.id}`}
              >
                {b.id === "growth" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF3B30] text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1">
                    {({nl:"Populair",en:"Popular",de:"Beliebt",fr:"Populaire",es:"Popular",ar:"\u0634\u0627\u0626\u0639"})[lang] || "Popular"}
                  </div>
                )}
                <div className="mb-4">
                  <Tag size={18} className="text-[#FF3B30] mb-3" />
                  <h3 className="text-lg font-bold text-white">{b.name}</h3>
                  <p className="text-xs text-white/40 mt-1 leading-relaxed">{b.description}</p>
                </div>
                <div className="mb-4">
                  <div className="text-sm text-white/40 line-through">&euro;{b.original_price.toLocaleString()}</div>
                  <div className="text-2xl font-black text-white">&euro;{b.price.toLocaleString()}</div>
                  <div className="inline-block mt-1 bg-[#FF3B30]/20 text-[#FF3B30] text-[10px] font-bold px-2 py-0.5 tracking-wider">
                    {({nl:"BESPAAR",en:"SAVE",de:"SPAREN",fr:"\u00c9CONOMIE",es:"AHORRO",ar:"\u0648\u0641\u0631"})[lang] || "SAVE"} &euro;{b.savings.toLocaleString()}
                  </div>
                </div>
                <div className="text-xs text-white/30 mb-4">
                  {b.services.map(s => SERVICE_LABELS[s] || s).join(" + ")}
                </div>
                <Button
                  size="sm"
                  className={`w-full rounded-sm text-xs font-bold ${b.id === "growth" ? "bg-[#FF3B30] hover:bg-[#e0352b] text-white" : "bg-white text-[#0A0A0A] hover:bg-white/90"}`}
                  onClick={() => handleBundleClick(b.id)}
                  data-testid={`bundle-buy-${b.id}`}
                >
                  {({nl:"Bundel kopen",en:"Buy Bundle",de:"Paket kaufen",fr:"Acheter le pack",es:"Comprar paquete",ar:"\u0634\u0631\u0627\u0621 \u0627\u0644\u062d\u0632\u0645\u0629"})[lang] || "Buy Bundle"} <ArrowRight size={14} className="ml-1" />
                </Button>
              </motion.div>
            ))}
          </div>
          {/* Compare button */}
          <div className="text-center mt-10">
            <Button
              variant="outline"
              className="rounded-sm border-white/20 text-white hover:bg-white/10 hover:text-white text-xs tracking-wider uppercase font-bold px-8"
              onClick={() => setShowCompare(true)}
              data-testid="compare-bundles-btn"
            >
              <BarChart size={14} className="mr-2" />
              {({nl:"Bundels vergelijken",en:"Compare Bundles",de:"Pakete vergleichen",fr:"Comparer les packs",es:"Comparar paquetes",ar:"\u0645\u0642\u0627\u0631\u0646\u0629 \u0627\u0644\u062d\u0632\u0645"})[lang] || "Compare Bundles"}
            </Button>
          </div>
        </div>
      </section>

      {/* Compare Dialog */}
      <BundleCompareDialog open={showCompare} onClose={() => setShowCompare(false)} bundles={bundles} lang={lang} onBuy={handleBundleClick} />

      {/* Approach */}
      <section className="py-20 md:py-28 bg-[#F9FAFB]" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3]">{t("approach.title")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-3">{t("approach.subtitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { title: t("approach.promise1_title"), desc: t("approach.promise1_desc") },
              { title: t("approach.promise2_title"), desc: t("approach.promise2_desc") },
              { title: t("approach.promise3_title"), desc: t("approach.promise3_desc") },
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-8 bg-white border border-border" data-testid={`approach-promise-${i}`}>
                <h3 className="text-xl font-bold mb-3">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="bg-white border border-border p-8 md:p-12">
            <h3 className="text-xl font-bold mb-6">{t("approach.usecases_title")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t("approach.usecases").map((uc, i) => (
                <div key={i} className="flex items-start gap-3 py-2" data-testid={`usecase-${i}`}>
                  <CheckCircle size={16} className="text-[#0A3FA3] mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{uc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 md:py-28 bg-white" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A3FA3]">{t("sectors.title")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mt-3">{t("sectors.subtitle")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t("sectors.items").map((sector, i) => {
              const Icon = sectorIcons[i];
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="p-6 border border-border hover:border-[#0A3FA3] transition-colors" data-testid={`sector-card-${i}`}>
                  <Icon size={24} className="text-[#0A3FA3] mb-4" />
                  <h3 className="text-lg font-bold mb-1">{sector.name}</h3>
                  <p className="text-sm text-muted-foreground">{sector.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0A3FA3]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-4">{t("cta.title")}</h2>
              <p className="text-white/70 mb-8 leading-relaxed">{t("cta.subtitle")}</p>
              <div className="space-y-3 mb-8">
                {t("cta.benefits").map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-white/80" />
                    <span className="text-sm text-white/90">{b}</span>
                  </div>
                ))}
              </div>
              <Button size="lg" className="bg-[#00BF4F] text-white hover:bg-[#00A843] rounded-sm px-8 py-3 font-bold" onClick={() => handleServiceClick("ai-quick-scan")} data-testid="cta-button">
                {t("cta.button")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            <div className="hidden md:block">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/4bfdb3dd-17d6-4a59-9a29-86d4ffc17442/images/b1cf818ac58054565234e15ebdfe153d040416ea0369504d788b57240813db53.png"
                alt="International AI consulting team"
                className="w-full aspect-[4/3] object-cover border-4 border-white/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_ai-governance-ready/artifacts/1y03ujcj_CleverTion.png" alt="CleverTion" className="h-10 w-auto" />
              <span className="text-xs text-white/50">{t("footer.tagline")}</span>
            </div>
            <div className="text-xs text-white/40">
              &copy; {new Date().getFullYear()} CleverTion. {t("footer.rights")}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


const ALL_SERVICES = [
  { id: "ai-quick-scan", label: "AI Quick Scan", desc: { nl: "Top-10 kansen, tijdsbesparing, 90-dagen roadmap", en: "Top-10 opportunities, time savings, 90-day roadmap", de: "Top-10 Chancen, Zeitersparnis, 90-Tage-Roadmap", fr: "Top 10 opportunités, gains de temps, feuille de route 90 jours", es: "Top 10 oportunidades, ahorro de tiempo, hoja de ruta 90 días", ar: "أفضل 10 فرص، توفير الوقت، خارطة طريق 90 يوماً" } },
  { id: "ai-pilot", label: "AI Pilot", desc: { nl: "Eén proces verbeteren, team testen, resultaat meten", en: "Improve one process, team testing, measure results", de: "Einen Prozess verbessern, Team testen, Ergebnis messen", fr: "Améliorer un processus, test d'équipe, mesurer les résultats", es: "Mejorar un proceso, prueba del equipo, medir resultados", ar: "تحسين عملية واحدة، اختبار الفريق، قياس النتائج" } },
  { id: "ai-workflow", label: "AI Workflow", desc: { nl: "CRM/ERP-integratie, e-mail classificatie, automatisering", en: "CRM/ERP integration, email classification, automation", de: "CRM/ERP-Integration, E-Mail-Klassifizierung, Automatisierung", fr: "Intégration CRM/ERP, classification email, automatisation", es: "Integración CRM/ERP, clasificación email, automatización", ar: "تكامل CRM/ERP، تصنيف البريد، الأتمتة" } },
  { id: "ai-training", label: "AI Training", desc: { nl: "Basistraining, prompttraining, AI-gebruiksbeleid", en: "Basic training, prompt training, AI usage policy", de: "Basistraining, Prompt-Training, KI-Nutzungsrichtlinien", fr: "Formation de base, prompts, politique d'utilisation IA", es: "Formación básica, prompts, política de uso de IA", ar: "تدريب أساسي، تدريب على الأوامر، سياسة استخدام AI" } },
  { id: "ai-governance", label: "AI Governance", desc: { nl: "AI Act readiness, risicobeoordeling, beleid", en: "AI Act readiness, risk assessment, policies", de: "AI Act Bereitschaft, Risikobewertung, Richtlinien", fr: "Préparation AI Act, évaluation des risques, politiques", es: "Preparación AI Act, evaluación de riesgos, políticas", ar: "الاستعداد لقانون AI، تقييم المخاطر، السياسات" } },
];

function BundleCompareDialog({ open, onClose, bundles, lang, onBuy }) {
  if (!bundles.length) return null;

  const txt = {
    nl: { title: "Bundels vergelijken", service: "Dienst", details: "Wat is inbegrepen", price: "Prijs", savings: "Besparing", included: "Inbegrepen", not_included: "Niet inbegrepen", buy: "Kopen", popular: "Populair", best: "Beste waarde" },
    en: { title: "Compare Bundles", service: "Service", details: "What's included", price: "Price", savings: "Savings", included: "Included", not_included: "Not included", buy: "Buy", popular: "Popular", best: "Best value" },
    de: { title: "Pakete vergleichen", service: "Service", details: "Was enthalten ist", price: "Preis", savings: "Ersparnis", included: "Enthalten", not_included: "Nicht enthalten", buy: "Kaufen", popular: "Beliebt", best: "Bester Wert" },
    fr: { title: "Comparer les packs", service: "Service", details: "Ce qui est inclus", price: "Prix", savings: "Économie", included: "Inclus", not_included: "Non inclus", buy: "Acheter", popular: "Populaire", best: "Meilleur rapport" },
    es: { title: "Comparar paquetes", service: "Servicio", details: "Qué incluye", price: "Precio", savings: "Ahorro", included: "Incluido", not_included: "No incluido", buy: "Comprar", popular: "Popular", best: "Mejor valor" },
    ar: { title: "مقارنة الحزم", service: "الخدمة", details: "ما هو مشمول", price: "السعر", savings: "التوفير", included: "مشمول", not_included: "غير مشمول", buy: "شراء", popular: "شائع", best: "أفضل قيمة" },
  };
  const t = txt[lang] || txt.en;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-auto p-0" data-testid="compare-dialog">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
            <BarChart size={20} className="text-[#0A3FA3]" />
            {t.title}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 pt-4">
          {/* Header row with bundle names */}
          <div className="grid grid-cols-5 gap-0 border border-border" data-testid="compare-table">
            {/* Top-left corner */}
            <div className="p-4 bg-[#F9FAFB] border-r border-b border-border">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.service}</span>
            </div>
            {/* Bundle column headers */}
            {bundles.map((b) => (
              <div key={b.id} className={`p-4 text-center border-b border-border ${b.id === "growth" ? "bg-[#0A3FA3]/5 border-x-2 border-x-[#0A3FA3]" : "bg-[#F9FAFB]"} ${b.id !== bundles[bundles.length-1].id ? "border-r" : ""}`}>
                {b.id === "growth" && (
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-[#FF3B30] text-white px-2 py-0.5 mb-2">{t.popular}</span>
                )}
                {b.id === "enterprise" && (
                  <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-[#0A3FA3] text-white px-2 py-0.5 mb-2">{t.best}</span>
                )}
                <div className="font-bold text-sm">{b.name}</div>
              </div>
            ))}

            {/* Service rows */}
            {ALL_SERVICES.map((svc) => (
              <React.Fragment key={svc.id}>
                <div className="p-3 border-r border-b border-border">
                  <div className="font-medium text-sm">{svc.label}</div>
                  <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{svc.desc[lang] || svc.desc.en}</div>
                </div>
                {bundles.map((b) => {
                  const included = b.services.includes(svc.id);
                  return (
                    <div key={`${b.id}-${svc.id}`} className={`p-3 flex items-center justify-center border-b border-border ${b.id === "growth" ? "bg-[#0A3FA3]/5 border-x-2 border-x-[#0A3FA3]" : ""} ${b.id !== bundles[bundles.length-1].id ? "border-r" : ""}`} data-testid={`compare-${b.id}-${svc.id}`}>
                      {included ? (
                        <div className="w-6 h-6 rounded-full bg-[#0A3FA3]/10 flex items-center justify-center">
                          <Check size={14} className="text-[#0A3FA3]" />
                        </div>
                      ) : (
                        <Minus size={14} className="text-border" />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {/* Price row */}
            <div className="p-3 border-r border-b border-border bg-[#F9FAFB]">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.price}</span>
            </div>
            {bundles.map((b) => (
              <div key={`price-${b.id}`} className={`p-3 text-center border-b border-border ${b.id === "growth" ? "bg-[#0A3FA3]/5 border-x-2 border-x-[#0A3FA3]" : ""} ${b.id !== bundles[bundles.length-1].id ? "border-r" : ""}`}>
                <div className="text-xs text-muted-foreground line-through">&euro;{b.original_price.toLocaleString()}</div>
                <div className="text-lg font-black text-[#0A0A0A]">&euro;{b.price.toLocaleString()}</div>
              </div>
            ))}

            {/* Savings row */}
            <div className="p-3 border-r border-b border-border bg-[#F9FAFB]">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t.savings}</span>
            </div>
            {bundles.map((b) => (
              <div key={`save-${b.id}`} className={`p-3 text-center border-b border-border ${b.id === "growth" ? "bg-[#0A3FA3]/5 border-x-2 border-x-[#0A3FA3]" : ""} ${b.id !== bundles[bundles.length-1].id ? "border-r" : ""}`}>
                <span className="inline-block text-[11px] font-bold text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5">&euro;{b.savings.toLocaleString()}</span>
              </div>
            ))}

            {/* CTA row */}
            <div className="p-3 border-r border-border bg-[#F9FAFB]" />
            {bundles.map((b) => (
              <div key={`cta-${b.id}`} className={`p-3 text-center ${b.id === "growth" ? "bg-[#0A3FA3]/5 border-x-2 border-x-[#0A3FA3]" : ""} ${b.id !== bundles[bundles.length-1].id ? "border-r border-border" : ""}`}>
                <Button
                  size="sm"
                  className={`rounded-sm text-xs w-full ${b.id === "growth" ? "bg-[#FF3B30] hover:bg-[#e0352b]" : "bg-[#0A3FA3] hover:bg-[#082F7A]"}`}
                  onClick={() => { onClose(); onBuy(b.id); }}
                  data-testid={`compare-buy-${b.id}`}
                >
                  {t.buy} <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
